import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from "@reduxjs/toolkit";

import {
  collection,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";

import { getAuth } from "firebase/auth";
import type { AppDispatch, RootState } from "./store";
import { auth, db, storage } from "../firebase";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

/* ───────────────── CONSTANTS ───────────────── */

const FOOD_COLLECTION = "FastFood";

/* ───────────────── TYPES ───────────────── */

export type FoodItem = {
  listingId: string;
  brandId: string;
  hostId: string;

  name?: string;
  categoryId?: string | number | null;   // now a CategoriesCollection id ("0904")
  category?: string;                     // its human label, for list columns
  subcategoryId?: number | null;
  productId?: number | null;

  stock?: number;
  available?: boolean;
  rate?: number;

  photos?: string[];
  videoUrl?: string;

  /* fresh handling — written by StoreFresh */
  unit?: string;
  netContent?: number;
  packagingId?: string;
  colorId?: string;
  storage?: string;
  shelfLifeDays?: number;
  harvestedOn?: string;
  bestBefore?: string;
  batchCode?: string;
  allergens?: string[];
  isOrganic?: boolean;

  isSpicy?: boolean;
  isVegan?: boolean;
  isVegetarian?: boolean;
  isGlutenFree?: boolean;
  spiceLevel?: number;
  prepTimeMinutes?: number;
  calories?: number;

  isPublished: boolean;
  isFlagged: boolean;
  isComplete: boolean;
  isReviewed: boolean;
  isDraft?: boolean;

  joinDate: string;
  createdAt?: any;
  updatedAt?: any;

  [key: string]: any;
};

type FoodState = {
  filteredListings: any;
  listings: FoodItem[];
  filteredFoods: FoodItem[];
  filters: {
    city: string | null;
    country: string | null;
  };
  currentFoodItem: FoodItem | null;
  unsubscribe?: () => void;
  loading: boolean;
  error: string | null;

  /* brand-scoped seller feed — renamed from brandListings so the keys
     line up with storeProductSlice's brandProducts/brandProductsLoading */
  brandFoodItems: FoodItem[];
  brandFoodItemsId: string | null;
  brandFoodLoading: boolean;
  brandUnsubscribe?: () => void;
};

const initialState: FoodState = {
  listings: [],
  currentFoodItem: null,
  unsubscribe: undefined,
  loading: false,
  error: null,
  filteredFoods: [],
  filters: {
    city: null,
    country: null,
  },
  brandFoodItems: [],
  brandFoodItemsId: null,
  brandFoodLoading: false,
  filteredListings: [],
};

/* ───────────────── HELPERS ───────────────── */

const toISO = (ts: any) =>
  ts?.toDate ? ts.toDate().toISOString() : ts ?? null;

const parseFoodItem = (
  id: string,
  raw: Record<string, any>
): FoodItem => {
  const { createdAt, updatedAt, ...safeRaw } = raw;

  return {
    listingId: id,
    brandId: raw.brandId ?? "",
    hostId: raw.hostId ?? "",

    name: raw.name ?? "",
    categoryId: raw.categoryId ?? null,
    category: raw.category ?? "",
    subcategoryId: raw.subcategoryId ?? null,
    productId: raw.productId ?? null,

    stock: raw.stock ?? 0,
    available: raw.available ?? true,
    rate: raw.rate ?? 0,

    photos: raw.photos ?? [],
    videoUrl: raw.videoUrl ?? "",

    unit: raw.unit ?? "",
    netContent: raw.netContent ?? 0,
    packagingId: raw.packagingId ?? "",
    colorId: raw.colorId ?? "",
    storage: raw.storage ?? "ambient",
    shelfLifeDays: raw.shelfLifeDays ?? 0,
    harvestedOn: raw.harvestedOn ?? "",
    bestBefore: raw.bestBefore ?? "",
    batchCode: raw.batchCode ?? "",
    allergens: raw.allergens ?? [],
    isOrganic: raw.isOrganic ?? false,

    isSpicy: raw.isSpicy ?? false,
    isVegan: raw.isVegan ?? false,
    isVegetarian: raw.isVegetarian ?? false,
    isGlutenFree: raw.isGlutenFree ?? false,
    spiceLevel: raw.spiceLevel ?? 0,
    prepTimeMinutes: raw.prepTimeMinutes ?? null,
    calories: raw.calories ?? null,

    isPublished: raw.isPublished ?? false,
    isFlagged: raw.isFlagged ?? false,
    isComplete: raw.isComplete ?? false,
    isReviewed: raw.isReviewed ?? false,
    isDraft: raw.isDraft ?? true,

    joinDate: raw.joinDate ?? new Date().toISOString(),

    createdAt: toISO(createdAt),
    updatedAt: toISO(updatedAt),

    ...safeRaw,
  };
};


export const upsertFoodMedia = createAsyncThunk<
  { listingId: string; downloadURLs: string[] },
  { listingId?: string; brandId?: string; files: File[]; type: "photo" | "video" }
>(
  "food/upsertMedia",
  async ({ listingId, brandId, files, type }, { dispatch }) => {
    const user = auth.currentUser;
    if (!user) throw new Error("Not logged in");

    let id = listingId;
    const uploaded: string[] = [];

    for (const file of files) {
      const folder = type === "photo" ? "photos" : "videos";
      const path = `${FOOD_COLLECTION}/${id || "new"}/${folder}/${Date.now()}_${file.name}`;

      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      uploaded.push(await getDownloadURL(storageRef));
    }

    if (!id) {
      const created = await dispatch(
        upsertFoodItem({
          brandId,
          data: {
            photos: type === "photo" ? uploaded : [],
            videoUrl: type === "video" ? uploaded[0] : "",
          },
        })
      ).unwrap();

      id = created.listingId;
    } else if (type === "photo") {
      const snap = await getDoc(doc(db, FOOD_COLLECTION, id));
      const existing: string[] = snap.exists() ? snap.data()?.photos ?? [] : [];

      await dispatch(
        upsertFoodItem({
          listingId: id,
          brandId,
          data: { photos: [...existing, ...uploaded] },
        })
      );
    } else {
      await dispatch(
        upsertFoodItem({ listingId: id, brandId, data: { videoUrl: uploaded[0] } })
      );
    }

    return { listingId: id!, downloadURLs: uploaded };
  }
);/* ───────────────── UPSERT ───────────────── */

export const upsertFoodItem = createAsyncThunk<
  FoodItem,
  {
    listingId?: string;
    brandId?: string;
    data: Omit<
      Partial<FoodItem>,
      | "hostId"
      | "joinDate"
      | "createdAt"
      | "updatedAt"
      | "videoUrl"
    >;
  }
>("food/upsert", async ({ listingId, brandId: brandIdArg, data }, { rejectWithValue }) => {
  try {
    const user = getAuth().currentUser;
    if (!user) throw new Error("User not authenticated");

    /* ── find brand ── */
    let brandId = brandIdArg;
    if (!brandId) {
      const partnerQuery = query(
        collection(db, "commercePartners"),
        where("userId", "==", user.uid)
      );
      const partnerSnap = await getDocs(partnerQuery);
      if (partnerSnap.empty) {
        throw new Error("No commerce partner found");
      }
      brandId = partnerSnap.docs[0].id;
    }

    /* ── clean data ── */
    const cleanData = Object.fromEntries(
      Object.entries(data).filter(([, v]) => v !== undefined)
    );

    const baseData = {
      ...cleanData,
      brandId,
      hostId: user.uid,
      updatedAt: serverTimestamp(),
    };

    /* ── update existing ── */
    if (listingId) {
      const refDoc = doc(db, FOOD_COLLECTION, listingId);
      await updateDoc(refDoc, baseData);

      const snap = await getDoc(refDoc);
      return parseFoodItem(snap.id, snap.data() || {});
    }

    /* ── create new ── */
    const payload = {
      ...baseData,
      joinDate: new Date().toISOString(),
      createdAt: serverTimestamp(),
      isPublished: cleanData.isPublished ?? false,
      isDraft: cleanData.isDraft ?? !(cleanData.isPublished ?? false),
      isFlagged: false,
      isComplete: false,
      isReviewed: false,
    };

    const colRef = collection(db, FOOD_COLLECTION);
    const newDoc = await addDoc(colRef, payload);
    await updateDoc(newDoc, { listingId: newDoc.id });

    const snap = await getDoc(newDoc);
    return parseFoodItem(newDoc.id, snap.data() || {});
  } catch (err: any) {
    return rejectWithValue(err.message || "upsert failed");
  }
});


/* Delete a single dish photo */
export const deleteFoodPhoto = createAsyncThunk<
  string,
  { listingId: string; photoUrl: string }
>(
  "food/deletePhoto",
  async ({ listingId, photoUrl }) => {
    const refDoc = doc(db, FOOD_COLLECTION, listingId);
    const snap = await getDoc(refDoc);

    if (!snap.exists()) throw new Error("Item not found");

    const photos = snap.data()?.photos || [];

    await updateDoc(refDoc, {
      photos: photos.filter((p: string) => p !== photoUrl),
    });

    if (photoUrl.includes("/o/")) {
      const encoded = photoUrl.split("/o/")[1].split("?")[0];
      await deleteObject(ref(storage, decodeURIComponent(encoded)));
    }

    return photoUrl;
  }
);

/* ───────────────── DELETE ───────────────── */

export const deleteFoodItem = createAsyncThunk<
  { listingId: string },
  string
>("food/delete", async (listingId) => {
  await deleteDoc(doc(db, FOOD_COLLECTION, listingId));
  return { listingId };
});

/* ───────────────── BRAND-SCOPED SELLER FEED ───────────────── */

/*
  FIX: the old listenToBrandFoodsFeed filtered on isPublished == true, so
  the seller dashboard could never show a draft — you'd save an item and
  watch it vanish. Matching storeProductSlice, the query is now
  brandId + hostId == uid: it returns drafts AND published items, and the
  hostId clause is the condition Firestore can prove from the query alone,
  so the listen isn't rejected with permission-denied.
*/
export const listenToBrandFoodFeed =
  (brandId: string) =>
  (dispatch: AppDispatch, getState: () => RootState): (() => void) => {
    if (!brandId) return () => {};

    const user = getAuth().currentUser;
    if (!user) {
      dispatch(setError("Not authenticated"));
      return () => {};
    }

    dispatch(setBrandFoodLoading(true));
    dispatch(setBrandFoodItemsId(brandId));

    const q = query(
      collection(db, FOOD_COLLECTION),
      where("brandId", "==", brandId),
      where("hostId", "==", user.uid)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const items: FoodItem[] = snap.docs.map((d) =>
          parseFoodItem(d.id, d.data())
        );
        dispatch(setBrandFoodItems(items));
        dispatch(setBrandFoodLoading(false));
      },
      (err) => {
        dispatch(setError(err.message));
        dispatch(setBrandFoodLoading(false));
      }
    );

    dispatch(setBrandUnsubscribe(unsub));
    return unsub;
  };

/* Stop the brand-scoped listener and clear the brand feed */
export const stopBrandFoodFeed =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    const { brandUnsubscribe } = getState().food;
    if (brandUnsubscribe) {
      brandUnsubscribe();
      dispatch(setBrandUnsubscribe(undefined));
      dispatch(setBrandFoodItemsId(null));
      dispatch(clearBrandFoodItems());
    }
  };

/* Back-compat aliases — the old plural names still resolve. */
export const listenToBrandFoodsFeed = listenToBrandFoodFeed;
export const stopBrandFoodsFeed = stopBrandFoodFeed;

/* ───────────────── PUBLIC LISTENERS ───────────────── */

/* Listen to all published dishes for a specific brand (storefront) */
export const listenToBrandFoods =
  ({ brandId }: { brandId: string }) =>
  (dispatch: AppDispatch) => {
    if (!brandId) return;

    const q = query(
      collection(db, FOOD_COLLECTION),
      where("brandId", "==", brandId),
      where("isPublished", "==", true)
    );

    return onSnapshot(q, (snap) => {
      dispatch(setListings(snap.docs.map((d) => parseFoodItem(d.id, d.data()))));
    });
  };

/* Listen to all dishes owned by the current host */
export const listenToHostFoods =
  ({ hostId }: { hostId: string }) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!hostId) return;

    const { unsubscribe } = getState().food;
    if (unsubscribe) {
      unsubscribe();
      dispatch(setUnsubscribe(undefined));
    }

    const q = query(
      collection(db, FOOD_COLLECTION),
      where("hostId", "==", hostId)
    );

    const unsub = onSnapshot(q, (snap) => {
      dispatch(setListings(snap.docs.map((d) => parseFoodItem(d.id, d.data()))));
    });

    dispatch(setUnsubscribe(unsub));
  };

/* Listen to all published dishes (marketplace feed) */
export const listenToPublishedFoods =
  () => async (dispatch: AppDispatch, getState: () => RootState) => {
    const { unsubscribe } = getState().food;
    if (unsubscribe) return;

    const brandsQuery = query(
      collection(db, "commercePartners"),
      where("isPublished", "==", true)
    );

    const brandsUnsub = onSnapshot(brandsQuery, (brandSnap) => {
      const publishedBrandIds = brandSnap.docs.map((d) => d.id);

      if (!publishedBrandIds.length) {
        dispatch(setListings([]));
        return;
      }

      /* Firestore "in" limit = 10, so chunk brand IDs */
      const chunkSize = 10;
      const chunks: string[][] = [];
      for (let i = 0; i < publishedBrandIds.length; i += chunkSize) {
        chunks.push(publishedBrandIds.slice(i, i + chunkSize));
      }

      let allListings: FoodItem[] = [];
      const listeners: (() => void)[] = [];

      chunks.forEach((chunk) => {
        const listingsQuery = query(
          collection(db, FOOD_COLLECTION),
          where("isPublished", "==", true),
          where("brandId", "in", chunk)
        );

        const unsubListings = onSnapshot(listingsQuery, (snap) => {
          const listings = snap.docs.map((d) => parseFoodItem(d.id, d.data()));

          allListings = [
            ...allListings.filter(
              (l) => !listings.some((nl) => nl.listingId === l.listingId)
            ),
            ...listings,
          ];

          dispatch(setListings(allListings));
        });

        listeners.push(unsubListings);
      });

      dispatch(
        setUnsubscribe(() => {
          brandsUnsub();
          listeners.forEach((u) => u());
        })
      );
    });
  };

/* Listen to a single dish document */
export const listenToFoodData =
  (listingId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!listingId) return;

    const { unsubscribe } = getState().food;
    if (unsubscribe) {
      unsubscribe();
      dispatch(setUnsubscribe(undefined));
    }

    const refDoc = doc(db, FOOD_COLLECTION, listingId);

    const unsub = onSnapshot(refDoc, (snap) => {
      if (!snap.exists()) {
        dispatch(setCurrentFoodItem(null));
        return;
      }
      dispatch(setCurrentFoodItem(parseFoodItem(snap.id, snap.data())));
    });

    dispatch(setUnsubscribe(unsub));
  };

/* ───────────────── CATEGORY LISTENERS ───────────────── */

export const listenToFoodsByCategory =
  (categoryId: string | number) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!categoryId) return;

    const { unsubscribe } = getState().food;
    if (unsubscribe) {
      unsubscribe();
      dispatch(setUnsubscribe(undefined));
    }

    const q = query(
      collection(db, FOOD_COLLECTION),
      where("isPublished", "==", true),
      where("categoryId", "==", categoryId)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const listings = snap.docs.map((d) => parseFoodItem(d.id, d.data()));

        if (listings.length === 0) {
          dispatch(listenToPublishedFoods());
          return;
        }

        dispatch(setListings(listings));
      },
      (error) => dispatch(setError(error.message))
    );

    dispatch(setUnsubscribe(unsub));
  };

export const listenToFoodCategoryGroup =
  (categoryIds: (string | number)[]) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!categoryIds.length) return;

    const { unsubscribe } = getState().food;
    if (unsubscribe) {
      unsubscribe();
      dispatch(setUnsubscribe(undefined));
    }

    const q = query(
      collection(db, FOOD_COLLECTION),
      where("isPublished", "==", true),
      where("categoryId", "in", categoryIds.slice(0, 10))
    );

    const unsub = onSnapshot(q, (snap) => {
      dispatch(setListings(snap.docs.map((d) => parseFoodItem(d.id, d.data()))));
    });

    dispatch(setUnsubscribe(unsub));
  };

/* ───────────────── FILTERS ───────────────── */

export const filterFoodsByBrandLocation =
  () =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    const { listings, filters } = getState().food;
    const { partners } = getState().commercePartners;

    if (!filters.city && !filters.country) {
      dispatch(setFilteredListings(listings));
      return;
    }

    const filtered = listings.filter((item) => {
      const brand = partners.find((p) => p.brandId === item.brandId);
      if (!brand) return false;

      const cityMatch = filters.city
        ? brand.address?.city?.toLowerCase().includes(filters.city.toLowerCase())
        : true;

      const countryMatch = filters.country
        ? brand.address?.country?.toLowerCase().includes(filters.country.toLowerCase())
        : true;

      return cityMatch && countryMatch;
    });

    dispatch(setFilteredListings(filtered));
  };

export const filterLocalFoodsByCountry =
  (country: string | null) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (!country) {
      dispatch(setFilteredListings(getState().food.listings));
      return;
    }

    try {
      const partnersQuery = query(
        collection(db, "commercePartners"),
        where("address.country", "==", country)
      );

      const partnersSnap = await getDocs(partnersQuery);
      const localBrandIds = partnersSnap.docs.map((d) => d.id);

      if (!localBrandIds.length) {
        dispatch(setFilteredListings([]));
        return;
      }

      const { listings } = getState().food;
      dispatch(setFilteredListings(listings.filter((l) => localBrandIds.includes(l.brandId))));
    } catch (error: any) {
      dispatch(setError(error.message));
    }
  };

/* ───────────────── SLICE ───────────────── */

const foodSlice = createSlice({
  name: "food",
  initialState,
  reducers: {
    setListings: (state, action: PayloadAction<FoodItem[]>) => {
      state.listings = action.payload;
    },
    setCurrentFoodItem: (state, action: PayloadAction<FoodItem | null>) => {
      state.currentFoodItem = action.payload;
    },
    setFilteredListings: (state, action: PayloadAction<FoodItem[]>) => {
      state.filteredListings = action.payload;
    },
    setUnsubscribe: (state, action: PayloadAction<(() => void) | undefined>) => {
      state.unsubscribe = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setFilters: (
      state,
      action: PayloadAction<{ city?: string | null; country?: string | null }>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFoodFilters: (state) => {
      state.filters = { city: null, country: null };
      state.filteredListings = state.listings;
    },
    setBrandFoodItems: (state, a: PayloadAction<FoodItem[]>) => {
      state.brandFoodItems = a.payload;
    },
    clearBrandFoodItems: (state) => {
      state.brandFoodItems = [];
    },
    setBrandFoodItemsId: (state, a: PayloadAction<string | null>) => {
      state.brandFoodItemsId = a.payload;
    },
    setBrandFoodLoading: (state, a: PayloadAction<boolean>) => {
      state.brandFoodLoading = a.payload;
    },
    setBrandUnsubscribe: (state, a: PayloadAction<(() => void) | undefined>) => {
      state.brandUnsubscribe = a.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(upsertFoodItem.fulfilled, (state, action) => {
        const upsertInto = (arr: FoodItem[]) => {
          const idx = arr.findIndex((l) => l.listingId === action.payload.listingId);
          if (idx !== -1) arr[idx] = action.payload;
          else arr.push(action.payload);
        };
        upsertInto(state.listings);
        // keep the seller feed in step so a save shows up before the
        // snapshot round-trips
        if (state.brandFoodItemsId === action.payload.brandId) {
          upsertInto(state.brandFoodItems);
        }
      })
      .addCase(deleteFoodItem.fulfilled, (state, action) => {
        state.listings = state.listings.filter(
          (l) => l.listingId !== action.payload.listingId
        );
        state.brandFoodItems = state.brandFoodItems.filter(
          (l) => l.listingId !== action.payload.listingId
        );
      });
  },
});

export const {
  setListings,
  setCurrentFoodItem,
  setUnsubscribe,
  setError,
  setFilters,
  setFilteredListings,
  resetFoodFilters,
  setBrandFoodItems,
  clearBrandFoodItems,
  setBrandFoodItemsId,
  setBrandFoodLoading,
  setBrandUnsubscribe,
} = foodSlice.actions;

export const foodReducer = foodSlice.reducer;
export default foodReducer;